import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonResponsiveWrapper, FlexForm, Preloader, useResponsive } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { State } from '@/common/types';
import AppButton from '@/components/controls/AppButton';
import { useSnackbar } from '@/modules/core/hooks';
import useStatesQuery from '@/modules/core/queries/useStates.query';
import { useClaimInfoStore } from '@/modules/transaction/components/ReimburseMe/store/useClaimInfo.store';
import { PaymentMethod } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import theme from '@/styles/theme';

import { Provider, useCreateProviderQuery, useUpdateProviderQuery } from '../useProviders.query';

import useProviderStore from './useProvider.store';
import useProviderFields, { PresetFields } from './useProviderFields';

interface ProviderFormData {
  adressLine1?: string;
  adressLine2?: string;
  city?: string;
  name?: string;
  state?: string;
  zipCode?: string;
}

const formatFormData = (data: ProviderFormData, states: State[]): Provider => ({
  name: data?.name || '',
  address: {
    line1: data?.adressLine1,
    line2: data?.adressLine2,
    city: data?.city,
    zipcode: data?.zipCode,
    state: {
      ...states.find((state) => state.id === data?.state),
    },
  },
});

interface Props {
  onCancel: () => void;
  refetch?: () => void;
  refetchPaymentMethods: () => void;
  presetFields?: PresetFields;
  provider?: Provider;
  paymentMethod?: PaymentMethod;
}

const AddProviderForm: React.FC<Props> = ({
  onCancel,
  presetFields,
  provider,
  paymentMethod,
  refetch,
  refetchPaymentMethods,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { setProviderId } = useProviderStore();
  const { handleSetProviderId } = useClaimInfoStore();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { save, isLoading } = useCreateProviderQuery((newlyCreatedProvider?: Provider) => {
    const providerId = toString(newlyCreatedProvider?.id);
    onCancel();
    handleAddPermanentSnackbar({
      text: `${newlyCreatedProvider?.name} ${t('added successfully.')}`,
      closeIcon: true,
    });
    refetchPaymentMethods();
    if (providerId) {
      setProviderId(providerId);
      handleSetProviderId(providerId);
    }
  });
  const onSuccessfullyUpdateProvider = useCallback(
    (updatedProvider: Provider) => {
      if (paymentMethod?.id && paymentMethod?.type && updatedProvider?.address) {
        refetchPaymentMethods();
      } else if (updatedProvider?.address) {
        refetchPaymentMethods();
      }
    },
    [paymentMethod, refetchPaymentMethods]
  );
  const { updateProvider, isLoading: isProviderLoading } = useUpdateProviderQuery(
    onSuccessfullyUpdateProvider
  );
  const { states } = useStatesQuery();
  const fields = useProviderFields(states, presetFields, provider);

  return (
    <FlexForm
      fields={fields}
      contentStyles={theme.formFieldsEdging}
      paddingless
      isNarrowLabelMode
      editMode
      onSubmit={async (data) => {
        const formData = formatFormData(data, states);

        if (provider?.id) {
          await updateProvider({ ...formData, id: provider?.id });
          onCancel();
          return;
        }

        await save(formData);
        if (refetch) refetch();
      }}
      footer={
        <ButtonResponsiveWrapper justify="end" align="center" pad={{ top: '40px' }}>
          <AppButton fill={isMobile} buttonType="secondary" type="button" width="130px" onClick={onCancel}>
            {t('Cancel')}
          </AppButton>

          <AppButton fill={isMobile} type="submit" width="130px">
            {isLoading || isProviderLoading ? <Preloader color="white" /> : t('Save')}
          </AppButton>
        </ButtonResponsiveWrapper>
      }
    />
  );
};

export default AddProviderForm;
